import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import ImageCompare from './ImageCompare';
import MetaballDemo from '../../Misc/MetaballDemo';
import ProgressStrips from './ProgressStrips';

const Page = styled.div`
  max-width: 860px;
  margin: 0 auto;
  padding: 100px 48px 80px;
  font-family: 'JetBrains Mono', monospace;

  @media (max-width: 768px) {
    padding: 80px 20px 60px;
  }
`;

const BackButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-size: 15px;
  font-family: 'JetBrains Mono', monospace;
  font-weight: 500;
  color: var(--text);
  opacity: 0.6;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  transition: opacity 0.2s ease;
  margin-bottom: 40px;
  display: block;

  &:hover { opacity: 1; }
`;

const HeroImage = styled.img`
  width: 100%;
  border-radius: 10px;
  margin-bottom: 36px;
  display: block;
`;

const Title = styled.h1`
  margin: 0 0 8px;
  font-size: 36px;
  font-weight: 700;
  color: var(--text);
`;

const Meta = styled.div`
  display: flex;
  gap: 20px;
  margin-bottom: 14px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text);
  opacity: 0.8;
`;

const TagRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 32px;
`;

const Tag = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: var(--text);
  opacity: 0.8;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  border: 1px solid var(--card-border);
  border-radius: 4px;
  padding: 2px 7px;
`;

const Divider = styled.hr`
  border: none;
  border-top: 1px solid var(--card-border);
  margin: 0 0 32px;
`;

const ImageGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 32px;
  margin-top: 40px;
  align-items: flex-start;

  & > * {
    flex: 1;
  }

  img {
    height: 220px;
    width: 100%;
    object-fit: cover;
  }

  @media (max-width: 600px) {
    & > * { flex: 1 1 100%; }
    img { height: auto; }
  }
`;

const GalleryItem = styled.figure`
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GalleryImage = styled.img`
  width: 100%;
  border-radius: 8px;
  border: 1px solid var(--card-border);
  display: block;
`;

const GalleryCaption = styled.figcaption`
  font-size: 15px;
  color: var(--text);
  opacity: 0.85;
  text-align: center;
  letter-spacing: 0.04em;
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  color: var(--text);
  opacity: 0.9;
  font-size: 16px;
  line-height: 1.8;

  body.dark & {
    opacity: 1;
  }

  ul {
    margin: 0;
    padding: 0 0 0 18px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  li {
    line-height: 1.7;
  }

  code {
    font-family: 'JetBrains Mono', monospace;
    font-size: inherit;
    background: var(--card-border);
    padding: 1px 5px;
    border-radius: 3px;
  }
`;

// Per-project content — fill in as you go
const PROJECT_DATA: Record<string, {
  title: string;
  tags: string[];
  image?: string;
  date?: string;
  course?: string;
  content: React.ReactNode;
}> = {
  weenix: {
    title: 'Weenix',
    tags: ['C', 'Operating Systems', 'Kernel'],
    image: '/projects/weenix/boot.png',
    date: '2024',
    course: 'CSCI 402 — Operating Systems',
    content: (
      <>
        <p>
          Weenix is a Unix-like operating system kernel written in C, built incrementally
          across three interdependent subsystems. Each subsystem had to be correct before the
          next could begin, which meant carefully reasoning about concurrency, resource
          lifetimes, and kernel/user boundaries at every step.
        </p>

        <h3>Processes &amp; Threads</h3>
        <p>
          The foundation: a preemptive scheduler with full process and kernel thread lifecycle
          management. This included implementing <code>fork</code>, <code>exec</code>,
          <code>exit</code>, and <code>wait</code>; building a run queue and context switcher;
          and writing the <code>idle</code> and <code>init</code> processes that bootstrap
          everything else. Synchronization primitives — mutexes and condition variables —
          were implemented from scratch on top of the scheduler.
        </p>

        <h3>VFS — Virtual File System</h3>
        <p>
          A filesystem-agnostic abstraction layer modeled after the real Linux VFS. Implemented
          the full vnode interface — <code>lookup</code>, <code>create</code>,{' '}
          <code>link</code>, <code>unlink</code>, <code>mkdir</code>, <code>rmdir</code>,{' '}
          <code>read</code>, <code>write</code>, <code>stat</code> — along with the file
          descriptor table, path resolution, and the <code>open</code>/<code>close</code>{' '}
          syscall layer. The VFS layer sits cleanly above any concrete filesystem.
        </p>

        <h3>Virtual Memory</h3>
        <p>
          The most complex subsystem: a demand-paged virtual memory system with per-process
          address spaces, a shadow object chain for copy-on-write, and a page fault handler
          that resolves faults by paging in from the backing object (anonymous memory or a
          vnode). Also implemented <code>mmap</code>, <code>munmap</code>, <code>brk</code>,
          and the full user/kernel address space split. With VM complete, the kernel can load
          and run ELF binaries in user space — the screenshot above is the kernel booting in
          QEMU.
        </p>
      </>
    ),
  },
  'parallel-raytracer': {
    title: 'Parallel Raytracer',
    tags: ['C++', 'OpenMP', 'MPI', 'Computer Graphics'],
    image: '/projects/parallel-raytracer/Reflections.png',
    content: (
      <>
        <p>
          A parallel raytracer built in C++ as a pair project for USC's Scientific Computing
          &amp; Visualization course (CSCI 596), exploring how raytracing workloads scale
          across shared-memory and distributed-memory parallelization models.
        </p>
        <p>Two parallelization strategies were implemented and benchmarked:</p>
        <ul>
          <li><strong>OpenMP</strong> — parallelizes the nested pixel loops across threads on a single node, with thread count configurable at runtime.</li>
          <li><strong>MPI</strong> — divides the image into horizontal strips across processes. A manager process collects rendered strips from workers and assembles the final image.</li>
        </ul>
        <p>
          Hard shadows come from treating each light as a single point — a shadow ray either
          hits an occluder or it doesn't, producing a perfectly sharp boundary. Soft shadows
          simulate an area light by splitting it into 100 randomly distributed sub-lights and
          averaging their contributions. Near the shadow edge, some sub-rays are blocked and
          some aren't, producing a natural penumbra.
        </p>
        <p>
          The same scene is rendered below with hard shadows on the left and soft shadows on
          the right — drag to compare. The difference is most visible at the base of the
          spheres where the penumbra softens the boundary.
        </p>
        <ImageCompare
          leftSrc="/projects/parallel-raytracer/soft_shadow_off.jpg"
          rightSrc="/projects/parallel-raytracer/soft_shadow_on.jpg"
          leftLabel="Hard shadows"
          rightLabel="Soft shadows"
          alt="Comparison of hard and soft shadows"
        />
        <p>Two anti-aliasing modes were implemented:</p>
        <ul>
          <li><strong>SSAA</strong> — subdivides each pixel into a regular grid and fires one ray per cell. Structured and predictable.</li>
          <li><strong>Random AA</strong> — fires rays with directions drawn from a Gaussian distribution. Stochastic — introduces visible grain at the same sample count and needs far more samples to converge.</li>
        </ul>
        <p>
          Random AA is on the left, SSAA on the right — the grain from stochastic sampling
          is immediately apparent along the sphere edges and in the shadows.
        </p>
        <ImageCompare
          leftSrc="/projects/parallel-raytracer/spheres_casting_soft_shadows_on_each_other_RandomAA.png"
          rightSrc="/projects/parallel-raytracer/spheres_casting_soft_shadows_on_each_other_SSAA.png"
          leftLabel="Random AA"
          rightLabel="SSAA"
          alt="Comparison of random anti-aliasing vs supersampling AA"
        />
        <p>
          Strong and weak scaling experiments were run on both a local machine (Intel i7-8750H,
          12 threads) and USC's CARC cluster (Xeon E5-2640v4). OpenMP scaling saturated around
          12 threads on the laptop, while CARC showed consistent improvement up to 20 threads.
          MPI efficiency gradually decreased with more processes as communication overhead grew,
          though an unexpected efficiency bump at 8 processes was observed — likely a caching
          effect from the cluster environment.
        </p>
        <div style={{ display: 'flex', gap: '32px', alignItems: 'flex-start', marginTop: '40px' }}>
          <GalleryItem style={{ flex: 1.6842, minWidth: 0 }}>
            <GalleryImage src="/projects/parallel-raytracer/StrongScalingPlot.png" alt="OpenMP strong scaling plot showing speedup vs thread count" style={{ width: '100%', height: 'auto' }} />
            <GalleryCaption>OpenMP strong scaling</GalleryCaption>
          </GalleryItem>
          <GalleryItem style={{ flex: 1.1903, minWidth: 0 }}>
            <GalleryImage src="/projects/parallel-raytracer/MPIStrongScaling.png" alt="MPI strong scaling plot showing efficiency vs process count" style={{ width: '100%', height: 'auto' }} />
            <GalleryCaption>MPI strong scaling</GalleryCaption>
          </GalleryItem>
        </div>
      </>
    ),
  },
  'processing-raytracer': {
    title: 'Raytracer',
    tags: ['Processing', 'Computer Graphics', 'Rendering'],
    image: '/projects/raytracer/Tetrahedron_floating_with_a_sphere_buddy.png',
    content: (
      <>
        <p>
          A software raytracer built in Processing, following Peter Shirley's{' '}
          <em>Ray Tracing in One Weekend</em>. The renderer outputs 800×600 images at 100
          samples per pixel with up to 20 recursive ray bounces, gamma correction, and
          per-pixel antialiasing — taking around 20 seconds per render.
        </p>
        <p>
          Three materials are implemented: Lambertian diffuse with cosine-weighted scattering,
          Metal with a configurable fuzz parameter for blurry reflections, and Dielectric with
          full Snell's law refraction, total internal reflection, and Schlick's approximation
          for angle-dependent reflectivity. A hollow glass sphere is constructed using the
          negative radius trick — a nested dielectric sphere with inverted normals — which
          correctly simulates a bubble.
        </p>
        <ImageGrid>
          <GalleryItem>
            <GalleryImage src="/projects/raytracer/3_spheres_closeup.png" alt="Three spheres close up showing diffuse, metallic, and dielectric materials" />
            <GalleryCaption>Three spheres — diffuse, metallic, dielectric</GalleryCaption>
          </GalleryItem>
          <GalleryItem>
            <GalleryImage src="/projects/raytracer/Sphere_on_Sphere_in_focus.png" alt="Two spheres with depth of field blur showing one in sharp focus" />
            <GalleryCaption>Depth of field</GalleryCaption>
          </GalleryItem>
        </ImageGrid>
        <p>
          Beyond the book, triangle intersection was added. The implementation uses
          ray-plane intersection followed by a cross product point-in-triangle test to check
          all three edges. This unlocks arbitrary mesh geometry — the tetrahedra in the renders
          below are built entirely from triangles, each assigned the same material interface as
          any other hittable object.
        </p>
        <ImageGrid>
          <GalleryItem>
            <GalleryImage src="/projects/raytracer/Tetrahedron_floating_with_a_sphere_buddy.png" alt="A pink tetrahedron and sphere rendered with reflections and soft shadows" />
            <GalleryCaption>Tetrahedron floating with a sphere buddy</GalleryCaption>
          </GalleryItem>
          <GalleryItem>
            <GalleryImage src="/projects/raytracer/Pink_tetraheadron_on_a_mirrored_surface.png" alt="A pink tetrahedron reflected in a mirrored ground surface" />
            <GalleryCaption>Pink tetrahedron on a mirrored surface</GalleryCaption>
          </GalleryItem>
        </ImageGrid>
        <p>
          Processing turned out to be a real constraint — no native multithreading for the
          render loop and some OOP limitations meant squeezing out performance required
          careful work.
        </p>
        <p>
          Source on{' '}
          <a href="https://github.com/hey-its-suvi/RaytracingInAWeekend" target="_blank" rel="noreferrer">
            GitHub
          </a>.
        </p>
      </>
    ),
  },
  'metaball-grid': {
    title: 'Metaball Grid',
    tags: ['CSS', 'Interactive', 'Animation'],
    content: (
      <>
        <p>
          An experiment in using the CSS metaball trick — <code>filter: blur()</code> on child
          elements combined with <code>filter: contrast()</code> on the parent — to drive
          selection state in a nav bar and a grid. Instead of the usual highlight (underline,
          bold, color change), the selected item is marked by a blob of light that travels
          through the layout.
        </p>
        <p>
          The organic feel comes from a few layered ideas. Multiple blobs share the same target
          but have different transition durations derived from their size — larger blobs are
          stiffer and arrive first, smaller ones lag behind. While one blob is already at the
          destination and another is still crossing, their blurred gradients overlap just enough
          that the contrast filter renders a connecting neck between them. That neck stretching
          and snapping is the metaball merge.
        </p>
        <p>
          At rest, the blobs run independent keyframe animations that wander off-center, pulse
          in size, and rotate — keeping the mass feeling alive rather than frozen. Scale
          transforms survive the blur pipeline (they deform the gaussian itself); border-radius
          changes don't, so only scale and translate are used for the organic shape.
        </p>
        <MetaballDemo />
        <p>
          References:{' '}
          <a href="https://varun.ca/metaballs/" target="_blank" rel="noreferrer">varun.ca/metaballs</a>
          {' · '}
          <a href="https://codepen.io/keithclark/pen/WNgRMQ" target="_blank" rel="noreferrer">Keith Clark on CodePen</a>
          {' · '}
          <a href="https://codeburst.io/creating-metaballs-loader-using-css-only-80090a0403da" target="_blank" rel="noreferrer">CSS-only metaballs loader</a>
        </p>
      </>
    ),
  },
  'raster-renderer': {
    title: 'Raster Renderer',
    tags: ['C++', 'Python', 'Computer Graphics', 'Rendering'],
    content: (
      <>
        <p>
          A software rasterizer built across two phases for USC's 3D Graphics &amp; Rendering
          course (CSCI 580) — first as a solo homework series in C++, then extended in a group
          final project in Python.
        </p>

        <h3>Phase 1 — Building the Pipeline</h3>
        <p>
          The homework series built the full rasterization pipeline incrementally from scratch.
          Each assignment added a layer on top of the last:
        </p>
        <ul>
          <li>Triangle rasterization using barycentric coordinates with a z-buffer for depth testing</li>
          <li>View and perspective projection matrices, viewport transform, and the full world → view → clip → raster pipeline</li>
          <li>Flat, Gouraud, and Phong shading with a JSON-driven scene and material system (ambient + directional lights, Phong illumination model)</li>
          <li>Perspective-correct texture mapping with bilinear filtering</li>
          <li>Multisampled anti-aliasing with 6 jittered sample patterns and weighted blending</li>
        </ul>
        <p>
          All matrix math was written from scratch in a custom C++ library
          with vector, matrix (with operator overloading),
          and camera classes that construct the view, projection, and viewport
          matrices from camera parameters. The test subject throughout was the Utah teapot.
        </p>
        <ProgressStrips strips={[
          { src: '/projects/raster-renderer/hw3_flat.png',        label: 'Flat shading'    },
          { src: '/projects/raster-renderer/hw4_gouraud.png',     label: 'Gouraud shading' },
          { src: '/projects/raster-renderer/hw4_phong.png',       label: 'Phong shading'   },
          { src: '/projects/raster-renderer/hw5_texture.png',     label: 'Texturing'       },
          { src: '/projects/raster-renderer/hw6_antialiased.png', label: 'Anti-aliasing'   },
        ]} />

        <h3>Phase 2 — Extensions</h3>
        <p>
          The final project extended the rasterizer in Python with normal mapping.
        </p>
        <ul>
          <li><strong>Normal mapping</strong> — a TBN frame (tangent, binormal, normal) is constructed per vertex and used to transform normal map perturbations into world space, adding surface detail without extra geometry</li>
        </ul>
        <p>
          Normal mapping works by perturbing the surface normal at each fragment using a
          texture lookup, so the lighting responds as if the geometry had detail that isn't
          actually there. A TBN matrix (tangent, bitangent, normal) is built per vertex to
          transform the map's tangent-space normals into world space. The effect is convincing
          head-on. The sphere below picks up bumps, ridges, and indentations from two
          different normal maps that would otherwise require thousands of extra triangles.
        </p>
        <ProgressStrips strips={[
          { src: '/projects/raster-renderer/beyond_sphere_plain.png', label: 'Plain'      },
          { src: '/projects/raster-renderer/beyond_sphere_norm1.png', label: 'Normal map 1' },
          { src: '/projects/raster-renderer/beyond_sphere_norm4.png', label: 'Normal map 2' },
        ]} />
        <p>
          The limitation shows at grazing angles. Normal mapping only changes the direction
          light bounces, so it doesn't change the actual silhouette of the mesh. At shallow
          viewing angles the outline of the sphere is still perfectly smooth, and the surface
          features flatten out because the perturbed normals end up pointing nearly parallel to
          the view direction. Techniques like parallax occlusion mapping or
          displacement mapping address this by actually shifting the visible surface, but
          they're more expensive.
        </p>
      </>
    ),
  },
  'stealth-in-the-shadows': {
    title: 'Stealth in the Shadows',
    tags: ['Unity', 'C#', 'Game Development'],
    image: '/projects/stealth-in-the-shadows/level-8-color-mixing.png',
    date: 'Summer 2024',
    course: 'CSCI 526 — Advanced Mobile Devices and Game Consoles',
    content: (
      <>
        <p>
          A 2D platformer built in Unity over ten weeks by a four-person team
          (<em>Crispy Fries</em>) for USC's CSCI 526. The core mechanic is switching between
          two forms. In <strong>normal form</strong> you're two tiles tall and move fast, with
          a tall jump that clears wide gaps and high ledges. Press <code>S</code> to{' '}
          <strong>shadow-dive</strong> — you flatten into the floor, become one tile tall, and
          can swim along walls and ceilings, slipping through narrow gaps the normal form can't
          fit through. Shadow form trades a shorter jump and slower ground speed for the
          ability to go anywhere a shadow can.
        </p>
        <p>
          Light is the danger. Plain white spotlights kill on contact in either form.{' '}
          <strong>Colored lights</strong> are non-lethal on their own, but standing in one
          forces you out of shadow form and locks you in normal form for as long as you're in
          the beam — so a colored cone across a narrow vertical gap is effectively a wall.{' '}
          <strong>Color mixing</strong> is the twist: where two complementary colored cones
          overlap, the intersection becomes white, and white kills. Most puzzles are about
          reading the level to figure out which lights to leave on, which to toggle off, and
          which form you need to be in at each step.
        </p>
        <p>
          You can play the gold build below, or open it{' '}
          <a
            href="https://csci-526-summer-2024.github.io/summer-2024-project-crispy-fries/AllBuilds/Gold%20Build/"
            target="_blank"
            rel="noreferrer"
          >
            in fullscreen here
          </a>.
        </p>
        <div style={{ position: 'relative', width: '100%', aspectRatio: '16 / 9', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--card-border)' }}>
          <iframe
            src="https://csci-526-summer-2024.github.io/summer-2024-project-crispy-fries/AllBuilds/Gold%20Build/"
            title="Stealth in the Shadows — playable WebGL build"
            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', border: 'none' }}
            allow="fullscreen; gamepad; autoplay"
            allowFullScreen
          />
        </div>

        <h3>My contributions</h3>
        <p>
          On this team I owned the <strong>light system</strong> and most of the{' '}
          <strong>UI</strong>, designed several levels (including my favorite, the snail), and
          contributed across movement feel, build setup, and design docs.
        </p>

        <h3>Light system</h3>
        <p>
          Lights are the core of every puzzle in this game, so the system grew from a single
          spotlight prefab into something with a lot of subtypes — permanent vs. toggleable,
          colored vs. white, static vs. rotating, and a 360° variant — all sharing the same
          underlying behavior. I built the original timer-based light prefab, the
          toggle/cooldown loop, and the color-mixing logic where overlapping colored cones
          combine into white (which kills). Later passes tuned the colors after playtests showed
          yellow was being misread as white.
        </p>
        <p>
          The light <em>selector</em> UI was a deliberately compact piece of teaching: a red
          bracket marks the active light, a green semicircle filling around the bulb shows
          remaining toggle time, and permanent (un-toggleable) lights have a distinct bulb color
          and no timer at all. Players figure out the rules without a tutorial popup — pressing
          A cycles to a permanent light and the selector visibly skips it. Level 9 (below) is
          the level that introduces all of this, designed so players can't progress without
          internalizing each piece.
        </p>
        <GalleryItem>
          <GalleryImage
            src="/projects/stealth-in-the-shadows/level-9-light-selector.png"
            alt="Level 9 showing the light selector UI — red bracket around the active light, green timer arc, and a non-toggleable light with no timer"
          />
          <GalleryCaption>Level 9 — light selector, timer arc, and a non-toggleable light</GalleryCaption>
        </GalleryItem>

        <h3>Levels</h3>
        <p>
          I designed Tutorial, Level 2 (which introduces glass tiles and forces players to
          parkour between shadow form and normal form), Tutorial 4 (the original colored-light
          intro), and the snail — Level 3. The snail is a gimmick level: the exit sits right
          next to the spawn but is walled off, so the only way out is to spiral the long way
          around in shadow form and come back. It exists partly because it's funny and partly
          because it gives players a low-pressure room to internalize the "hold one direction
          and the shadow form swims along the surface" mechanic introduced one level earlier.
          It got direct positive callouts in playtests.
        </p>
        <GalleryItem>
          <GalleryImage
            src="/projects/stealth-in-the-shadows/level-3-snail.png"
            alt="Level 3 — the snail level. The exit is right next to the player but the path spirals out and around the shell."
          />
          <GalleryCaption>Level 3 — the snail</GalleryCaption>
        </GalleryItem>
        <p>
          I also built one of the cloud levels — clouds block light but can't be stood on, and
          they're the first dynamic obstacle in the game, so the level had to teach the rule
          and the timing without text. Tuning the cloud speed to match player movement (so
          holding right is enough to stay in its shadow) came out of analytics showing players
          were dying to mistimed jumps rather than to the puzzle itself.
        </p>
        <GalleryItem>
          <GalleryImage
            src="/projects/stealth-in-the-shadows/level-12-clouds.png"
            alt="Level 12 — clouds blocking light. The player must stay inside the cloud's shadow as it drifts under a spotlight."
          />
          <GalleryCaption>Level 12 — clouds as moving safe zones</GalleryCaption>
        </GalleryItem>

        <h3>UI</h3>
        <p>
          I built most of the front-end Unity work: the main menu, the level-select grid (with
          per-level star tracking), the controls page, the in-level pause menu, the death and
          win states, and the proportional light timer bars that replaced the original
          fixed-width ones once playtests showed players couldn't tell short-timer lights from
          long-timer ones. I also swapped in a custom WebGL build template after the default
          Unity one was clipping the game on fullscreen.
        </p>

        <h3>Other</h3>
        <ul>
          <li>
            <strong>Coyote time</strong> — players were missing edge jumps because Unity's
            grounded check is unforgiving. Added a short post-edge grace window so jumps feel
            responsive without being floaty.
          </li>
          <li>
            <strong>Shadow-dive on glass</strong> — wired the rule that glass tiles block
            shadow-diving, with a camera shake to communicate "no" without a popup.
          </li>
          <li>
            <strong>Form switching off-floor</strong> — late playtest fix to allow exiting
            shadow form mid-air, which removed a class of "stuck" feeling players reported.
          </li>
          <li>
            <strong>Death UI &amp; input pausing</strong> — early-game polish so death felt
            like feedback instead of a soft-lock.
          </li>
          <li>
            <strong>GDD</strong> — initial sketch diagrams, mechanics research and matrix,
            single-mechanic challenge matrix, tutorialization matrix, UI flow diagram, and
            level descriptions.
          </li>
        </ul>
      </>
    ),
  },
};

const ProjectPage: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const data = projectId ? PROJECT_DATA[projectId] : null;

  if (!data) {
    return (
      <Page>
        <BackButton onClick={() => navigate(-1)}>← back</BackButton>
        <Title>Project not found</Title>
      </Page>
    );
  }

  return (
    <Page>
      <BackButton onClick={() => navigate(-1)}>← back</BackButton>
      {data.image && <HeroImage src={data.image} alt={data.title} />}
      <Title>{data.title}</Title>
      {(data.date || data.course) && (
        <Meta>
          {data.course && <span>{data.course}</span>}
          {data.date && <span>{data.date}</span>}
        </Meta>
      )}
      <TagRow>
        {data.tags.map(tag => <Tag key={tag}>{tag}</Tag>)}
      </TagRow>
      <Divider />
      <Body>{data.content}</Body>
    </Page>
  );
};

export default ProjectPage;
